import { ShieldCheck, Target, FileCheck2, UserCheck, Globe2, Lock, TrendingUp, Network, Layers } from 'lucide-react';

const LOGO_COLORS = ['#074EDC', '#FF8C00', '#4AAC00', '#ED0216'];

const RING_ICONS = [
  { Icon: Target,      label: 'Target'   },
  { Icon: FileCheck2,  label: 'Evidence' },
  { Icon: UserCheck,   label: 'Verify'   },
  { Icon: Globe2,      label: 'Global'   },
  { Icon: Lock,        label: 'Secure'   },
  { Icon: TrendingUp,  label: 'Improve'  },
  { Icon: Network,     label: 'Network'  },
  { Icon: Layers,      label: 'Layers'   },
];

const RADIUS = 100; // px from center to icon center

function getPosition(index: number, total: number): { x: number; y: number } {
  const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
  return {
    x: Math.cos(angle) * RADIUS,
    y: Math.sin(angle) * RADIUS,
  };
}

export function CircularGraphics() {
  return (
    <div className="cg-container">
      <style>{`
        .cg-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          min-height: 320px;
        }

        .cg-scene {
          position: relative;
          width: 260px;
          height: 260px;
        }

        /* Dashed ring */
        .cg-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1.5px dashed rgba(7, 78, 220, 0.3);
          animation: cg-spin-ring 30s linear infinite;
        }

        .cg-ring-inner {
          position: absolute;
          inset: 20px;
          border-radius: 50%;
          border: 1px solid rgba(7, 78, 220, 0.12);
        }

        /* Center icon */
        .cg-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(7,78,220,0.1) 0%, rgba(7,78,220,0.18) 100%);
          border: 1.5px solid rgba(7, 78, 220, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 28px rgba(7, 78, 220, 0.22);
          z-index: 2;
        }

        /* Orbit icons */
        .cg-icon-item {
          position: absolute;
          top: 50%;
          left: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }

        .cg-icon-item:hover {
          transform: translate(-50%, -50%) scale(1.18) !important;
          opacity: 1 !important;
          z-index: 3;
        }

        .cg-icon-bg {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid rgba(7, 78, 220, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 10px rgba(7, 78, 220, 0.1);
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }

        .cg-icon-item:hover .cg-icon-bg {
          border-color: rgba(7, 78, 220, 0.7);
          box-shadow: 0 0 18px rgba(7, 78, 220, 0.3);
        }

        .cg-icon-label {
          font-size: 9px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #475569;
          white-space: nowrap;
          pointer-events: none;
          transition: color 0.25s ease;
        }

        .cg-icon-item:hover .cg-icon-label {
          color: #074EDC;
        }

        @keyframes cg-spin-ring {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .cg-ring {
            animation: none;
          }
        }
      `}</style>

      <div className="cg-scene">
        <div className="cg-ring" />
        <div className="cg-ring-inner" />

        <div className="cg-center">
          <ShieldCheck size={22} color="#074EDC" strokeWidth={1.5} />
        </div>

        {RING_ICONS.map(({ Icon, label }, i) => {
          const { x, y } = getPosition(i, RING_ICONS.length);
          return (
            <div
              key={label}
              className="cg-icon-item"
              style={{
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                opacity: 0.85,
              }}
            >
              <div className="cg-icon-bg">
                <Icon size={15} color={LOGO_COLORS[i % LOGO_COLORS.length]} strokeWidth={1.5} />
              </div>
              <span className="cg-icon-label">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
